import { LocaleCode, translation } from './translation';
import { FieldError } from './ui';

type SubscriptionLimit = 150 | 250 | 500 | 750 | 1000 | 9999;

const subscriptionLimits: SubscriptionLimit[] = [150, 250, 500, 750, 1000, 9999];

type MerchantStatus = 'normal' | 'demo' | 'suspended';

const MerchantStatuses: MerchantStatus[] = ['normal', 'demo', 'suspended'];

type MerchantImagePosition = number | null;

interface SubscriptionUsage {
	limit: SubscriptionLimit;
	used: number;
}

interface MerchantImage {
	fileName: string;
	position: MerchantImagePosition;
}

interface MerchantTexts {
	specialisation: string;
	history: string;
	businessHours?: string;
}

interface Merchant {
	id: number;
	name: string;
	status?: MerchantStatus; // returned only in the admin and super.
	legalName: string;
	slug: string;
	email?: string; // returned only in the admin and super (omitted in the showcase to deter scraping).
	vatNumber: string;
	fiscalCode?: string; // returned only in the admin and super.
	sdiCode?: string | null; // returned only in the admin and super.
	pec?: string | null; // returned only in the admin and super.
	phoneNumber: string | null;
	landlineNumber: string | null;
	whatsappNumber: string | null;
	countryID: number;
	subRegion: string | null;
	city: string;
	address: string;
	zipCode: string;
	website: string | null;
	isPending?: boolean; // returned only in the admin and super.
	isFollowing: boolean;
	logo: MerchantImage;
	cover: MerchantImage | null;
	texts?: Record<LocaleCode, MerchantTexts> | null; // returned only in the admin and super.
	specialisation?: string | null; // returned only in the showcase.
	history?: string | null; // returned only in the showcase.
	businessHours?: string | null; // returned only in the showcase.
	subscriptionLimit?: SubscriptionLimit; // returned only in the admin and super.
	subscriptionEnd?: string; // returned only in the admin and super.
	productUsed?: number; // returned only in the admin and super.
	features?: string[]; // returned only in the admin and super.
	createdAt: string;
	userFirstName?: string; // returned only in the super.
	userLastName?: string; // returned only in the super.
	userEmail?: string; // returned only in the super.
	userCreatedAt?: string; // returned only in the super.
}

interface RecentlyActiveMerchant {
	id: number;
	name: string;
	slug: string;
	logo: MerchantImage;
	specialisation: string;
	history: string;
	uploadedProductsCount: number;
}

interface MerchantToAdd {
	userEmail: string;
	userFirstName: string;
	userLastName: string;
	merchantName: string;
	merchantLegalName: string;
	vatNumber: string;
	fiscalCode: string;
	SDICode: string | null;
	PEC: string | null;
	merchantLanguage: string;
	merchantEmail: string;
	merchantCountryID: number;
	merchantSubRegion: string | null;
	merchantCity: string;
	merchantAddress: string;
	merchantZipCode: string;
	specialisation: string;
	history: string;
	subscriptionLimit: number;
	subscriptionEnd: string;
	status: MerchantStatus;
}

interface MerchantToSet {
	email: string;
	phoneNumber: string | null;
	landlineNumber: string | null;
	whatsappNumber: string | null;
	website: string | null;
	texts: Record<LocaleCode, MerchantTexts>;
}

interface MerchantSettings {
	languageCode: string;
}

const validateMerchantToAdd = (m: MerchantToAdd, locale: LocaleCode) => {
	const t = translation[locale];

	validateMerchantUser(m.userFirstName, m.userLastName, m.userEmail, locale);

	validateMerchantBusiness(
		m.merchantName,
		m.merchantLegalName,
		m.vatNumber,
		m.fiscalCode,
		m.SDICode,
		m.PEC,
		m.merchantCountryID,
		m.merchantSubRegion,
		m.merchantCity,
		m.merchantAddress,
		m.merchantZipCode,
		locale,
	);

	if (m.merchantLanguage == null) {
		throw new Error(t['Merchant language is required']);
	}
	if (m.merchantEmail == null || m.merchantEmail === '') {
		throw new Error(t['Merchant email is required']);
	}
	if (m.merchantEmail.length > 120) {
		throw new Error(t['Merchant email cannot be longer than 120 characters']);
	}

	if (m.specialisation == null || m.specialisation === '') {
		throw new Error(t['Specialisation is required']);
	}
	if (m.specialisation.length > 150) {
		throw new Error(t['Specialisation cannot be longer than 150 characters']);
	}
	if (m.history == null || m.history === '') {
		throw new Error(t['History is required']);
	}
	if (m.history.length > 1200) {
		throw new Error(t['History cannot be longer than 1200 characters']);
	}

	validateSubscriptionEnd(m.subscriptionEnd, locale);
};

const validateMerchantToSet = (m: MerchantToSet, locale: LocaleCode) => {
	const t = translation[locale];

	// email.
	if (m.email == null) {
		throw new FieldError(t['Email is required'], 'email');
	}
	if (m.email.length > 120) {
		throw new FieldError(t['Email cannot be longer than 120 characters'], 'email');
	}

	// phone number.
	if (m.phoneNumber != null) {
		if (m.phoneNumber.length > 50) {
			throw new FieldError(
				t['Phone number cannot be longer than 50 characters'],
				'phone-number',
			);
		}
	}

	// landline number.
	if (m.landlineNumber != null) {
		if (m.landlineNumber.length > 50) {
			throw new FieldError(
				t['Landline number cannot be longer than 50 characters'],
				'landline-number',
			);
		}
	}

	// whatsapp number.
	if (m.whatsappNumber != null) {
		if (m.whatsappNumber.length > 50) {
			throw new FieldError(
				t['Whatsapp number cannot be longer than 50 characters'],
				'whatsapp-number',
			);
		}
	}

	// website.
	if (m.website != null) {
		if (m.website.length > 50) {
			throw new FieldError(t['Website cannot be longer than 50 characters'], 'website');
		}
	}

	// Text length validation is intentionally skipped because
	// merchant texts may already exceed the max length enforced at
	// creation due to automatic translations.
	for (const lang in m.texts) {
		const l = lang as LocaleCode;
		let language = '';
		if (l === 'it') {
			language = t['Italian'];
		} else if (l === 'en') {
			language = t['English'];
		} else if (l === 'fr') {
			language = t['French'];
		}
		language = language.toLowerCase();

		// specialisation.
		if (m.texts[l].specialisation == null) {
			throw new FieldError(
				t[`The field "Specialisation" in the {lang} language is required`].replace(
					'{lang}',
					language,
				),
				'specialisation',
			);
		}

		// history.
		if (m.texts[l].history == null) {
			throw new FieldError(
				t[`The field "History" in the {lang} language is required`].replace(
					'{lang}',
					language,
				),
				'history',
			);
		}
	}
};

const validateMerchantUser = (
	firstName: string,
	lastName: string,
	email: string,
	locale: LocaleCode,
) => {
	const t = translation[locale];
	if (firstName == null || firstName === '') {
		throw new Error(t['First name is required']);
	}
	if (firstName.length > 100) {
		throw new Error(t['First name cannot be longer than 100 characters']);
	}
	if (lastName == null || lastName === '') {
		throw new Error(t['Last name is required']);
	}
	if (lastName.length > 100) {
		throw new Error(t['Last name cannot be longer than 100 characters']);
	}
	if (email == null || email === '') {
		throw new Error(t['Email is required']);
	}
	if (email.length > 120) {
		throw new Error(t['Email cannot be longer than 120 characters']);
	}
};

const validateMerchantBusiness = (
	merchantName: string | undefined,
	merchantLegalName: string | undefined,
	vatNumber: string | undefined,
	fiscalCode: string | undefined,
	SDICode: string | null | undefined,
	PEC: string | null | undefined,
	merchantCountryID: number | undefined,
	merchantSubRegion: string | null,
	merchantCity: string | undefined,
	merchantAddress: string | undefined,
	merchantZipCode: string | undefined,
	locale: LocaleCode,
) => {
	const t = translation[locale];
	if (merchantName == null || merchantName === '') {
		throw new Error(t['Merchant name is required']);
	}
	if (merchantName.length > 100) {
		throw new Error(t['Merchant name cannot be longer than 100 characters']);
	}
	if (merchantLegalName == null || merchantLegalName === '') {
		throw new Error(t['Merchant legal name is required']);
	}
	if (merchantLegalName.length > 120) {
		throw new Error(t['Merchant legal name cannot be longer than 120 characters']);
	}
	if (vatNumber == null || vatNumber === '') {
		throw new Error(t['VAT number is required']);
	}
	if (vatNumber.length > 30) {
		throw new Error(t['VAT number cannot be longer than 30 characters']);
	}
	if (fiscalCode == null || fiscalCode === '') {
		throw new Error(t['Fiscal code is required']);
	}
	if (fiscalCode.length > 30) {
		throw new Error(t['Fiscal code cannot be longer than 30 characters']);
	}
	if (SDICode != null && SDICode.length > 7) {
		throw new Error(t['SDI code cannot be longer than 7 characters']);
	}
	if (PEC != null && PEC.length > 120) {
		throw new Error(t['PEC cannot be longer than 120 characters']);
	}
	if (merchantCountryID == null) {
		throw new Error(t['Country is required']);
	}
	if (merchantSubRegion != null && merchantSubRegion === '') {
		throw new Error(t['Subregion is required']);
	}
	if (merchantCity == null || merchantCity === '') {
		throw new Error(t['Merchant city is required']);
	}
	if (merchantCity.length > 50) {
		throw new Error(t['Merchant city cannot be longer than 50 characters']);
	}
	if (merchantAddress == null || merchantAddress === '') {
		throw new Error(t['Merchant address is required']);
	}
	if (merchantAddress.length > 150) {
		throw new Error(t['Merchant address cannot be longer than 150 characters']);
	}
	if (merchantZipCode == null || merchantZipCode === '') {
		throw new Error(t['Merchant zip code is required']);
	}
	if (merchantZipCode.length > 10) {
		throw new Error(t['Merchant zip code cannot be longer than 10 characters']);
	}
};

const validateSubscriptionEnd = (end: string, locale: LocaleCode) => {
	const t = translation[locale];
	if (end === '') {
		throw new Error(t['Subscription end is required']);
	}
	const d = new Date(end);
	if (isNaN(d.getTime())) {
		throw new Error(t['Subscription end date is not valid']);
	}
	const now = new Date();
	if (d < now) {
		throw new Error(t['Subscription end cannot be in the past']);
	}
};

export {
	validateMerchantToAdd,
	validateMerchantToSet,
	MerchantStatuses,
	subscriptionLimits,
	validateMerchantUser,
	validateMerchantBusiness,
	validateSubscriptionEnd,
};
export type {
	Merchant,
	MerchantImage,
	RecentlyActiveMerchant,
	MerchantSettings,
	MerchantToAdd,
	MerchantToSet,
	MerchantTexts,
	MerchantStatus,
	SubscriptionUsage,
	SubscriptionLimit,
};
